import { create } from 'zustand'
import {
  EMPTY_GRAPH,
  cloneGraph,
  addNode as gAddNode,
  updateNode as gUpdateNode,
  removeNode as gRemoveNode,
  addEdge as gAddEdge,
  linkDestinationWithEdge as gLinkDestinationWithEdge,
  updateEdge as gUpdateEdge,
  removeEdge as gRemoveEdge,
  splitEdgeAtPoint as gSplitEdgeAtPoint,
  addAnchor as gAddAnchor,
  updateAnchor as gUpdateAnchor,
  removeAnchor as gRemoveAnchor,
  addDestination as gAddDestination,
  updateDestination as gUpdateDestination,
  removeDestination as gRemoveDestination,
  addZone as gAddZone,
  updateZone as gUpdateZone,
  removeZone as gRemoveZone,
  addBeacon as gAddBeacon,
  updateBeacon as gUpdateBeacon,
  removeBeacon as gRemoveBeacon,
} from './graph.js'

export const EDITOR_MODE = {
  TWO_D: '2d',
  THREE_D: '3d',
  SPLIT: 'split',
  AR_PREVIEW: 'ar',
}

export const TOOL = {
  SELECT: 'select',
  ADD_NODE: 'add-node',
  ADD_EDGE: 'add-edge',
  ADD_ANCHOR: 'add-anchor',
  ADD_DESTINATION: 'add-destination',
  ADD_ZONE: 'add-zone',
  ADD_BEACON: 'add-beacon',
  PAN: 'pan',
}

export const LAYER = {
  ASSET: 'asset',
  NODES: 'nodes',
  EDGES: 'edges',
  ANCHORS: 'anchors',
  DESTINATIONS: 'destinations',
  ZONES: 'zones',
  BEACONS: 'beacons',
}

const initialLayers = {
  [LAYER.ASSET]: true,
  [LAYER.NODES]: true,
  [LAYER.EDGES]: true,
  [LAYER.ANCHORS]: true,
  [LAYER.DESTINATIONS]: true,
  [LAYER.ZONES]: true,
  [LAYER.BEACONS]: true,
}

const HISTORY_LIMIT = 50

export const useSpatialStore = create((set, get) => ({
  floor: null,
  graph: cloneGraph(EMPTY_GRAPH),
  selection: null,
  pendingEdgeFrom: null,
  hoveredId: null,
  layers: { ...initialLayers },
  mode: EDITOR_MODE.SPLIT,
  tool: TOOL.SELECT,
  routePreview: null,
  validation: { blockers: [], warnings: [], info: [] },
  dirty: false,
  bottomTab: 'validation',
  history: [],
  redoStack: [],
  roofClipFraction: 1,
  walkthrough: { running: false, progress: 0, speed: 1, follow: false, eyeHeightM: 0.65 },

  setRoofClipFraction(value) {
    const clamped = Math.max(0.05, Math.min(1, Number(value)))
    set({ roofClipFraction: Number.isFinite(clamped) ? clamped : 1 })
  },

  setFloor(floor) {
    set({ floor })
  },

  hydrateGraph(graph) {
    set({
      graph: cloneGraph({ ...EMPTY_GRAPH, ...graph }),
      selection: null,
      pendingEdgeFrom: null,
      dirty: false,
      history: [],
      redoStack: [],
    })
  },

  reset() {
    set({
      floor: null,
      graph: cloneGraph(EMPTY_GRAPH),
      selection: null,
      pendingEdgeFrom: null,
      hoveredId: null,
      mode: EDITOR_MODE.SPLIT,
      tool: TOOL.SELECT,
      routePreview: null,
      validation: { blockers: [], warnings: [], info: [] },
      dirty: false,
      history: [],
      redoStack: [],
      walkthrough: { running: false, progress: 0, speed: 1, follow: false, eyeHeightM: 0.65 },
    })
  },

  setMode(mode) {
    set({ mode })
  },

  setTool(tool) {
    set({ tool, pendingEdgeFrom: null })
  },

  toggleLayer(layer) {
    set((s) => ({ layers: { ...s.layers, [layer]: !s.layers[layer] } }))
  },

  setLayer(layer, visible) {
    set((s) => ({ layers: { ...s.layers, [layer]: visible } }))
  },

  setSelection(selection) {
    set({ selection })
  },

  setHovered(id) {
    set({ hoveredId: id })
  },

  setBottomTab(tab) {
    set({ bottomTab: tab })
  },

  setPendingEdgeFrom(nodeId) {
    set({ pendingEdgeFrom: nodeId })
  },

  setRoutePreview(route) {
    set({
      routePreview: route,
      walkthrough: {
        running: false,
        progress: 0,
        speed: get().walkthrough.speed,
        follow: get().walkthrough.follow,
        eyeHeightM: get().walkthrough.eyeHeightM ?? 0.65,
      },
    })
  },

  startWalkthrough() {
    const { walkthrough, routePreview } = get()
    if (!routePreview?.path?.length) return
    const fromEnd = walkthrough.progress >= 1 - 1e-3
    set({
      walkthrough: {
        ...walkthrough,
        running: true,
        progress: fromEnd ? 0 : walkthrough.progress,
      },
    })
  },

  pauseWalkthrough() {
    set((s) => ({ walkthrough: { ...s.walkthrough, running: false } }))
  },

  resetWalkthrough() {
    set((s) => ({ walkthrough: { ...s.walkthrough, running: false, progress: 0 } }))
  },

  setWalkthroughProgress(progress) {
    const value = Math.max(0, Math.min(1, Number(progress) || 0))
    set((s) => ({ walkthrough: { ...s.walkthrough, progress: value } }))
  },

  setWalkthroughSpeed(speed) {
    const value = Math.max(0.25, Math.min(6, Number(speed) || 1))
    set((s) => ({ walkthrough: { ...s.walkthrough, speed: value } }))
  },

  setWalkthroughFollow(follow) {
    set((s) => ({ walkthrough: { ...s.walkthrough, follow: !!follow } }))
  },

  setWalkthroughEyeHeight(meters) {
    const value = Math.max(0.1, Math.min(10, Number(meters)))
    set((s) => ({
      walkthrough: {
        ...s.walkthrough,
        eyeHeightM: Number.isFinite(value) ? value : 0.65,
      },
    }))
  },

  setValidation(validation) {
    set({ validation })
  },

  markClean() {
    set({ dirty: false })
  },

  undo() {
    const { history, graph, redoStack } = get()
    if (history.length === 0) return
    const previous = history[history.length - 1]
    set({
      graph: previous,
      history: history.slice(0, -1),
      redoStack: [...redoStack, graph].slice(-HISTORY_LIMIT),
      selection: null,
      pendingEdgeFrom: null,
    })
  },

  redo() {
    const { redoStack, graph, history } = get()
    if (redoStack.length === 0) return
    const next = redoStack[redoStack.length - 1]
    set({
      graph: next,
      redoStack: redoStack.slice(0, -1),
      history: [...history, graph].slice(-HISTORY_LIMIT),
      selection: null,
      pendingEdgeFrom: null,
    })
  },

  addNode(input) {
    return mutateGraph(set, get, (graph) => gAddNode(graph, input), { selectKind: 'node' })
  },
  updateNode(id, patch) {
    return mutateGraph(set, get, (graph) => gUpdateNode(graph, id, patch))
  },
  removeNode(id) {
    return mutateGraph(set, get, (graph) => gRemoveNode(graph, id), { clearSelection: true })
  },

  addEdge(input) {
    return mutateGraph(set, get, (graph) => gAddEdge(graph, input), { selectKind: 'edge' })
  },
  linkDestinationWithEdge(fromNodeId, destId) {
    return mutateGraph(
      set,
      get,
      (graph) => gLinkDestinationWithEdge(graph, fromNodeId, destId),
      { selectKind: 'edge' },
    )
  },
  updateEdge(id, patch) {
    return mutateGraph(set, get, (graph) => gUpdateEdge(graph, id, patch))
  },
  removeEdge(id) {
    return mutateGraph(set, get, (graph) => gRemoveEdge(graph, id), { clearSelection: true })
  },
  splitEdgeAtPoint(edgeId, point, nodeInput) {
    const before = get().graph
    const oldEdge = (before.edges || []).find((e) => e.id === edgeId)
    const created = mutateGraph(
      set,
      get,
      (graph) => gSplitEdgeAtPoint(graph, edgeId, point, nodeInput),
      { selectKind: 'node' },
    )
    if (!oldEdge || !created) return created

    const { routePreview, graph: nextGraph } = get()
    if (!routePreview?.path?.length) return created

    const path = routePreview.path
    const a = oldEdge.from_node_id
    const b = oldEdge.to_node_id
    const newId = created.id
    let changed = false
    const next = []
    for (let i = 0; i < path.length; i += 1) {
      next.push(path[i])
      const cur = path[i]
      const peek = path[i + 1]
      if (peek === undefined) continue
      const matchesForward = cur === a && peek === b
      const matchesReverse = cur === b && peek === a
      if (matchesForward || matchesReverse) {
        next.push(newId)
        changed = true
      }
    }
    if (!changed) return created

    let totalDistance = 0
    for (let i = 1; i < next.length; i += 1) {
      const from = nextGraph.nodes.find((n) => n.id === next[i - 1])
      const to = nextGraph.nodes.find((n) => n.id === next[i])
      if (!from || !to) continue
      const dx = (from.x ?? 0) - (to.x ?? 0)
      const dz = (from.z ?? 0) - (to.z ?? 0)
      const dy = (from.y ?? 0) - (to.y ?? 0)
      totalDistance += Math.sqrt(dx * dx + dy * dy + dz * dz)
    }

    set({ routePreview: { ...routePreview, path: next, distance: totalDistance } })
    return created
  },

  addAnchor(input) {
    return mutateGraph(set, get, (graph) => gAddAnchor(graph, input), { selectKind: 'anchor' })
  },
  updateAnchor(id, patch) {
    return mutateGraph(set, get, (graph) => gUpdateAnchor(graph, id, patch))
  },
  removeAnchor(id) {
    return mutateGraph(set, get, (graph) => gRemoveAnchor(graph, id), { clearSelection: true })
  },

  addDestination(input) {
    return mutateGraph(set, get, (graph) => gAddDestination(graph, input), { selectKind: 'destination' })
  },
  updateDestination(id, patch) {
    return mutateGraph(set, get, (graph) => gUpdateDestination(graph, id, patch))
  },
  removeDestination(id) {
    return mutateGraph(set, get, (graph) => gRemoveDestination(graph, id), { clearSelection: true })
  },

  addZone(input) {
    return mutateGraph(set, get, (graph) => gAddZone(graph, input), { selectKind: 'zone' })
  },
  updateZone(id, patch) {
    return mutateGraph(set, get, (graph) => gUpdateZone(graph, id, patch))
  },
  removeZone(id) {
    return mutateGraph(set, get, (graph) => gRemoveZone(graph, id), { clearSelection: true })
  },

  addBeacon(input) {
    return mutateGraph(set, get, (graph) => gAddBeacon(graph, input), { selectKind: 'beacon' })
  },
  updateBeacon(id, patch) {
    return mutateGraph(set, get, (graph) => gUpdateBeacon(graph, id, patch))
  },
  removeBeacon(id) {
    return mutateGraph(set, get, (graph) => gRemoveBeacon(graph, id), { clearSelection: true })
  },
}))

function mutateGraph(set, get, fn, { selectKind = null, clearSelection = false } = {}) {
  const state = get()
  const result = fn(state.graph)
  const nextGraph = result.graph || result
  const created = result.created
  set({
    graph: nextGraph,
    history: [...state.history, state.graph].slice(-HISTORY_LIMIT),
    redoStack: [],
    dirty: true,
    selection: selectKind && created
      ? { kind: selectKind, id: created.id }
      : clearSelection
        ? null
        : state.selection,
    pendingEdgeFrom: clearSelection ? null : state.pendingEdgeFrom,
  })
  return created
}
